import type { BundleVariantsType } from '../../../types/bundles';
import type { BundleOfferAppearance, BundleOfferDto } from '@/queries/bundles/bundleOffers.schema';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { resolveBundleOfferAppearance } from '@/queries/bundles/bundleOffers.schema';
import { userBundleOffersQueryOptions } from '@/queries/bundles/bundles.queries';
import { orderIt } from '../../../utils/buildBundleGroups';

export interface BundleCardView {
  className?: string;
  offer: BundleOfferDto;
  variant: BundleVariantsType;
}

export interface ShopBundleGroups {
  isLoading: boolean;
  promoOffers: BundleOfferDto[];
  vipOffers: BundleOfferDto[];
  baseOffers: BundleOfferDto[];
  promoDefaultOffers: BundleOfferDto[];
  gamificationShopOffers: BundleOfferDto[];
}

const hasAppearance = (offer: BundleOfferDto, appearance: BundleOfferAppearance): boolean =>
  resolveBundleOfferAppearance(offer.offer.presentation) === appearance;
const isVipOffer = (offer: BundleOfferDto): boolean => hasAppearance(offer, 'vip');
const isPromoOffer = (offer: BundleOfferDto): boolean => hasAppearance(offer, 'promo');
const isGamificationShopOffer = (offer: BundleOfferDto): boolean => hasAppearance(offer, 'gamificationShop');
const isDefaultOffer = (offer: BundleOfferDto): boolean => offer.offer.isDefault ?? false;
const isDefaultPromoProduct = (offer: BundleOfferDto): boolean => (offer.product.presentation?.isDefaultPromo && !offer.offer.isDefault) ?? false;

// Strips badge/beforeSalePrice so default-promo products render as plain promo cards until a real promo offer is assigned.
const withoutPresentationOldPriceAndBadge = (bundleOffer: BundleOfferDto): BundleOfferDto => ({
  ...bundleOffer,
  offer: {
    ...bundleOffer.offer,
    presentation: bundleOffer.offer.presentation
      ? {
          ...bundleOffer.offer.presentation,
          badge: undefined,
        }
      : bundleOffer.offer.presentation,
  },
  product: {
    ...bundleOffer.product,
    presentation: bundleOffer.product.presentation
      ? {
          ...bundleOffer.product.presentation,
          beforeSalePrice: undefined,
        }
      : bundleOffer.product.presentation,
  },
});

/**
 * Categories GET /users/me/bundle-offers into the lists the Shop modal renders:
 *  - `vipOffers`: VIP offers, ordered by `orderIt`.
 *  - `promoOffers`: assigned Customer.io promo offers.
 *  - `gamificationShopOffers`: assigned Customer.io gamification shop offers.
 *  - `promoDefaultOffers`: product-level default promo bundles for the 1st/2nd/3rd purchase lifecycle.
 *  - `baseOffers`: regular Shop bundles. Product-level default promos are excluded while their
 *    promo lifecycle is active and return here once the backend marks them as default offers.
 */
export const useBundleGroups = (): ShopBundleGroups => {
  const { data: bundleOffers, isLoading } = useQuery(userBundleOffersQueryOptions.list());

  const vipOffers = useMemo(
    () => (bundleOffers || []).filter(isVipOffer).sort((a, b) => orderIt(a) - orderIt(b)),
    [bundleOffers],
  );
  const promoOffers = useMemo(
    () => (bundleOffers || []).filter(offer => !isVipOffer(offer) && isPromoOffer(offer)).sort((a, b) => orderIt(a) - orderIt(b)),
    [bundleOffers],
  );
  const gamificationShopOffers = useMemo(
    () => (bundleOffers || []).filter(isGamificationShopOffer).sort((a, b) => orderIt(a) - orderIt(b)),
    [bundleOffers],
  );
  const nonSpecialOffers = useMemo(
    () => (bundleOffers || []).filter(offer => !isVipOffer(offer) && !isPromoOffer(offer) && !isGamificationShopOffer(offer)).sort((a, b) => orderIt(a) - orderIt(b)),
    [bundleOffers],
  );
  const baseOffers = useMemo(
    () => nonSpecialOffers.filter(offer => !isDefaultPromoProduct(offer) || isDefaultOffer(offer)),
    [nonSpecialOffers],
  );
  // Product used as default promo then return all product offers without any presentation beforeSalePrice and badge,
  // to be shown in Special Offers when the user has no assigned promo offers from customer.
  const promoDefaultOffers = useMemo(
    () => nonSpecialOffers
      .filter(offer => !isDefaultOffer(offer) && isDefaultPromoProduct(offer))
      .map(withoutPresentationOldPriceAndBadge),
    [nonSpecialOffers],
  );

  return {
    isLoading,
    vipOffers,
    promoOffers,
    baseOffers,
    promoDefaultOffers,
    gamificationShopOffers,
  };
};
